// Distance unit resolution and formatting (F-037).
//
// The locator picks the unit system: KM or MI explicitly, or AUTO. AUTO is
// resolved by the VISITOR's region — a US visitor to a global brand sees miles,
// a German visitor sees kilometres — because the unit is a reader convenience,
// not brand data. We derive region from navigator.language (no geo-IP, no
// storage), defaulting to metric, which is what most of the world reads.

import type { Units } from "../data/types";

/** The resolved, concrete unit system actually used for rendering. */
export type ResolvedUnit = "KM" | "MI";

// Regions that read road distances in miles. Everyone else gets kilometres.
const MILE_REGIONS = new Set(["US", "GB", "MM", "LR"]);

/** Resolves the configured [units] to a concrete KM/MI, deriving AUTO from region. */
export function resolveUnit(units: Units, languageTag: string | undefined): ResolvedUnit {
  if (units === "KM") {
    return "KM";
  }
  if (units === "MI") {
    return "MI";
  }
  return regionPrefersMiles(languageTag) ? "MI" : "KM";
}

/** Extracts the region subtag (e.g. `en-US` -> `US`) and checks the miles set. */
function regionPrefersMiles(languageTag: string | undefined): boolean {
  if (!languageTag) {
    return false;
  }
  const region = languageTag.split("-")[1]?.toUpperCase();
  return region != null && MILE_REGIONS.has(region);
}

const METERS_PER_MILE = 1609.344;

/**
 * Formats a distance for display, choosing a sensible precision: sub-unit
 * distances round to one decimal, larger ones to whole units. Returns null for
 * a null input rather than "0" — an absent distance is honestly blank (the API
 * only populates distance when the visitor searched a center).
 */
export function formatDistance(
  meters: number | null,
  unit: ResolvedUnit,
  languageTag: string | undefined,
): string | null {
  if (meters == null) {
    return null;
  }
  const value = unit === "MI" ? meters / METERS_PER_MILE : meters / 1000;
  const decimals = value < 10 ? 1 : 0;
  const formatted = new Intl.NumberFormat(intlLocaleOrDefault(languageTag), {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(value);
  return `${formatted} ${unit === "MI" ? "mi" : "km"}`;
}

/**
 * `navigator.language` is not guaranteed to be a valid BCP-47 tag: Chromium on a
 * POSIX/C-locale Linux host (e.g. a CI runner, a kiosk) reports "en-US@posix",
 * which `Intl.NumberFormat` rejects with a RangeError. That exception thrown
 * mid-render used to take the whole search results list — and the camera fit —
 * down with it. An invalid tag falls back to `undefined`, i.e. the browser's own
 * default locale, which is the closest honest answer to "format it how this
 * visitor's browser would".
 */
function intlLocaleOrDefault(languageTag: string | undefined): string | undefined {
  if (languageTag == null) {
    return undefined;
  }
  try {
    new Intl.NumberFormat(languageTag);
    return languageTag;
  } catch {
    return undefined;
  }
}
